import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { useWallet } from '../../context/WalletContext';
import { fileAPI, innovatorAPI, projectAPI } from '../../services/api';
import { useContractServices } from '../../context/ContractServicesContext';
import { ethers } from 'ethers';
import { toast } from 'react-toastify';
import { Copy, Check, ExternalLink } from 'lucide-react';

function ProjectForm({ onSubmitSuccess }) {
  const { walletAddress, chainId } = useWallet();
  const { services, executeContractTransaction, isReady } = useContractServices();
  const [registeredInnovators, setRegisteredInnovators] = useState([]);
  const [totalCreatorIUs, setTotalCreatorIUs] = useState(0);
  const [remainingCreatorIUs, setRemainingCreatorIUs] = useState(0);
  const [currentInnovator, setCurrentInnovator] = useState(null);
  
  // New state for storing the hardcoded total supply from the contract
  const [contractTotalSupply, setContractTotalSupply] = useState({
    raw: null,
    formatted: '0'
  });
  const [loadingContractData, setLoadingContractData] = useState(true);
  
  // Form validation state
  const [validationErrors, setValidationErrors] = useState({
    projectName: '',
    pricePerUnit: '',
    ownershipDistribution: ''
  });
  
  const [primaryCreatorAllocation, setPrimaryCreatorAllocation] = useState(100); // Start with 100% if single creator
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0); 

  const DOMAINS = [
    "AI & Machine Learning",
    "DeFi & Finance",
    "Climate & Sustainability",
    "Gaming & Entertainment",
    "Infrastructure & Tooling",
    "Digital Arts & NFTs",
    "Security & Privacy",
    "Social Impact & Community",
    "Web3 & Blockchain",
    "Education & Knowledge",
    "Health & Wellness",
    "Supply Chain & Logistics",
    "Governance & DAOs",
    "Identity & Credentials",
    "Metaverse & Virtual Worlds",
    "Hardware & IoT",
    "Other"
  ];

  const ROLES = [
    "CTO - Technical",
    "CEO - Business",
    "CPO - Product",
    "CMO - Marketing",
    "COO - Operations",
    "CFO - Finance",
    "CRO - Revenue",
    "CLO - Legal",
    "CPO - People",
    "CDO - Data",
    "CISO - Security",
    "CCO - Creative",
    "Product Director",
    "Product Manager",
    "Product Designer",
    "User Experience Designer",
    "Technical Director",
    "Engineering Manager",
    "Scrum Master",
    "Front End Developer",
    "Back End Developer",
    "Full Stack Developer",
    "Blockchain Developer",
    "Smart Contract Developer",
    "Infrastructure Engineer",
    "Information Security Director",
    "Security Engineer",
    "Marketing Director",
    "Marketing Manager",
    "Community Manager",
    "People Director",
    "Recruiter"
  ];

  

  // Fetch registered innovators and current innovator when component mounts
  useEffect(() => {
    const fetchInnovators = async () => {
      try {
        const response = await innovatorAPI.getAll();
        
        // First find and set the current innovator (project owner) by comparing wallet addresses
        const currentInnovatorData = response.data.find(
          innovator => innovator.walletAddress.toLowerCase() === walletAddress.toLowerCase()
        );
        
        if (currentInnovatorData) {
          console.log('Found project owner:', currentInnovatorData.name, 'with wallet:', currentInnovatorData.walletAddress);
          setCurrentInnovator({
            innovatorId: currentInnovatorData._id,
            name: currentInnovatorData.name,
            email: currentInnovatorData.contactEmail,
            walletAddress: currentInnovatorData.walletAddress,
            role: 'Project Owner',
            iuAllocation: 0
          });
        } else {
          console.log('No project owner found for wallet:', walletAddress);
        }

        // Then map remaining innovators excluding the project owner
        const innovators = response?.data
          .filter(innovator => innovator.walletAddress.toLowerCase() !== walletAddress.toLowerCase())
          .map(innovator => ({
            value: innovator._id,
            label: innovator.name,
            email: innovator.contactEmail,
            walletAddress: innovator.walletAddress,
            _name: innovator.name,
            _walletAddress: innovator.walletAddress
          }));
        
        setRegisteredInnovators(innovators);
      } catch (error) {
        console.error('Error fetching innovators:', error);
      }
    };
    
    if (walletAddress) {
      fetchInnovators();
    } else {
      console.log('No wallet address available');
    }
  }, [walletAddress]);

  // Fetch total supply from contract when services are ready
  useEffect(() => {
    const fetchContractData = async () => {
      if (isReady && services.innovationUnits) {
        setLoadingContractData(true);
        try {
          // Fetch the global total supply from the contract
          const totalSupply = await services.innovationUnits.getInnovationUnitsTotalSupply();
          setContractTotalSupply(totalSupply);
          
          // Update the form data with the contract's total supply
          setFormData(prevData => ({
            ...prevData,
            totalInnovationUnits: totalSupply.formatted
          }));
        } catch (error) {
          toast.error('Failed to load contract data. Please refresh and try again.');
        } finally {
          setLoadingContractData(false);
        }
      }
    };
    
    fetchContractData();
  }, [isReady, services]);

  const [formData, setFormData] = useState({
    // Project Overview
    projectName: '',
    shortDescription: '',
    detailedDescription: '',
    relevantUrls: [''],

    // Tokenomics & Investment Details
    totalInnovationUnits: '', // This will be populated from the contract
    pricePerUnit: '',
    ownershipDistribution: {
      contributors: '',
      investors: '',
      creators: '', // Only keep creators field
    },

    // Domain & Target Audience
    primaryDomain: '',
    targetedPlatforms: [''],
    intendedAudience: [],

    // Team & Contributors
    creators: [], // Changed from contributors to creators

    // Collaboration & Access
    openForCollaboration: false,
    contributionGuidelines: '',

    // Additional Information
    projectRoadmap: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);

  // Update remaining creator IUs when total or individual allocations change
  useEffect(() => {
    // Calculate the sum of relative allocations for all creators except the primary creator
    const totalRelativeAllocation = formData.creators.reduce(
      (sum, creator) => sum + Number(creator.iuAllocation || 0), 
      0
    );
    
    // Primary creator gets the remaining percentage (out of 100%)
    const newPrimaryCreatorAllocation = Math.max(0, 100 - totalRelativeAllocation);
    setPrimaryCreatorAllocation(newPrimaryCreatorAllocation);
    
    // For display purposes, calculate the available IUs based on the creators allocation percentage
    const maxCreatorIUs = Number(formData.ownershipDistribution.creators || 0);
    setTotalCreatorIUs(maxCreatorIUs);
    
    // For UI display and validation, show the remaining allocation as percentage of creators allocation
    setRemainingCreatorIUs(newPrimaryCreatorAllocation);
  }, [formData.creators, formData.ownershipDistribution.creators]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear validation error when field is being edited
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleNestedChange = (object, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [object]: {
        ...prev[object],
        [field]: value,
      },
    }));
  };

  const handleArrayChange = (arrayName, index, value) => {
    const newArray = [...formData[arrayName]];
    newArray[index] = value;
    setFormData((prev) => ({
      ...prev,
      [arrayName]: newArray,
    }));
  };

  const addArrayItem = (arrayName, emptyItem = '') => {
    setFormData((prev) => ({
      ...prev,
      [arrayName]: [...prev[arrayName], emptyItem],
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    const newArray = [...formData[arrayName]];
    newArray.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      [arrayName]: newArray,
    }));
  };

  // Modified handleCreatorChange for relative allocation percentages
  const handleCreatorChange = (index, field, value) => {
    const newCreators = [...formData.creators];

    if (field === 'innovator') {
      // When an innovator is selected from dropdown
      const selectedInnovator = registeredInnovators?.find(i => i?.value === value?.value);
      newCreators[index] = {
        ...newCreators[index],
        innovatorId: selectedInnovator?.value,
        name: selectedInnovator?._name, // Use the stored name
        email: selectedInnovator?.email,
        walletAddress: selectedInnovator?._walletAddress // Use the stored wallet address
      };
    } else if (field === 'iuAllocation') {
      // Validate IU allocation - now treated as percentage of total creator allocation
      const allocation = Number(value);
      
      // Calculate total allocation from all other creators (excluding current one and primary)
      const otherCreatorsTotal = formData.creators.reduce((sum, creator, i) => 
        i !== index ? sum + Number(creator?.iuAllocation || 0) : sum, 
        0
      );
      
      // Check if allocation would exceed 100%
      if (allocation + otherCreatorsTotal > 100) {
        toast.error(`Total allocation cannot exceed 100%. Maximum available: ${100 - otherCreatorsTotal}%`);
        return;
      }
      
      newCreators[index] = {
        ...newCreators[index],
        iuAllocation: value
      };
    } else {
      newCreators[index] = {
        ...newCreators[index],
        [field]: value
      };
    }

    setFormData(prev => ({
      ...prev,
      creators: newCreators
    }));
  };

  // Modified addCreator for relative allocation percentages
  const addCreator = () => {
    // Check if there's any allocation percentage available
    if (primaryCreatorAllocation <= 0) {
      alert('No more allocation percentage available. Please reduce other creators\' allocations first.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      creators: [
        ...prev.creators,
        {
          innovatorId: '',
          name: '',
          role: '',
          email: '',
          walletAddress: '',
          iuAllocation: '' // This will be a percentage of the total creator allocation
        }
      ]
    }));
  };

  const removeCreator = (index) => {
    const newCreators = [...formData.creators];
    // When removing a creator, that creator's allocation becomes available again
    // No need to make further adjustments as the useEffect will recalculate
    newCreators.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      creators: newCreators,
    }));
  };

  // const onDrop = useCallback((acceptedFiles) => {
  //   setFiles(acceptedFiles);
  // }, []);

  // const { getRootProps, getInputProps } = useDropzone({
  //   onDrop,
  //   accept: {
  //     'image/*': ['.jpeg', '.jpg', '.png'],
  //     'application/pdf': ['.pdf'],
  //     'application/vnd.openxmlformats-officedocument.presentationml.presentation':
  //       ['.pptx'],
  //     'application/vnd.ms-powerpoint': ['.ppt'],
  //     'application/msword': ['.doc'],
  //     'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
  //       ['.docx'],
  //   },
  // });


  // Validate form fields
  const validateForm = () => {
    const errors = {
      projectName: '',
      pricePerUnit: '',
      ownershipDistribution: ''
    };
    let isValid = true;

    // Validate project name
    if (!formData.projectName.trim()) {
      errors.projectName = 'Project Title is required';
      isValid = false;
    }

    // Validate price per unit
    if (!formData.pricePerUnit || Number(formData.pricePerUnit) <= 0) {
      errors.pricePerUnit = 'Initial Price must be greater than 0';
      isValid = false;
    }

    // Validate ownership distribution sums to 100%
    const creators = Number(formData.ownershipDistribution.creators) || 0;
    const investors = Number(formData.ownershipDistribution.investors) || 0;
    const contributors = Number(formData.ownershipDistribution.contributors) || 0;
    const total = creators + investors + contributors;
    
    if (total !== 100) {
      errors.ownershipDistribution = `Total distribution must equal 100%. Current total: ${total}%`;
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  // Validate current step to determine if next button should be enabled
  const validateStep = (step) => {
    switch (step) {
      case 0:
        return !!formData.projectName.trim(); // Project Overview step - require project name
      case 1:
        // Tokenomics step - validate price and ownership distribution
        // First check if price is valid
        if (!formData.pricePerUnit || Number(formData.pricePerUnit) <= 0) {
          return false;
        }
        
        // Then check if ownership distribution sums to 100%
        const creators = Number(formData.ownershipDistribution.creators) || 0;
        const investors = Number(formData.ownershipDistribution.investors) || 0;
        const contributors = Number(formData.ownershipDistribution.contributors) || 0;
        const total = creators + investors + contributors;
        
        return total === 100;
      default:
        return true;
    }
  };

  // Handle changes to ownership distribution fields
  const handleDistributionChange = (field, value) => {
    // Clear validation error when field is being edited
    if (validationErrors.ownershipDistribution) {
      setValidationErrors(prev => ({
        ...prev,
        ownershipDistribution: ''
      }));
    }
    
    handleNestedChange('ownershipDistribution', field, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submitting
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      // Upload files if any
      const fileUrls = [];
      if (files.length > 0) {
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);
          const response = await fileAPI.upload(formData);
          fileUrls.push(response.data.fileUrl);
        }
      }

      // Calculate relative allocations for creators (as percentages of total creator allocation)
      const totalCreatorAllocation = Number(formData.ownershipDistribution.creators);
      
      // Primary creator (index 0) gets the remaining percentage
      // Secondary creators get their specified percentages
      const allCreatorsRelative = [
        {
          walletAddress: walletAddress,
          role: 'Project Owner',
          iuAllocation: primaryCreatorAllocation // This is a percentage of the creator allocation
        },
        ...formData.creators.map(creator => ({
          walletAddress: creator.walletAddress,
          role: creator.role,
          iuAllocation: Number(creator.iuAllocation) // Already a percentage of creator allocation
        }))
      ];
      
      // Convert relative percentages to absolute token amounts for the API
      const allCreators = allCreatorsRelative.map(creator => ({
        walletAddress: creator.walletAddress,
        role: creator.role,
        iuAllocation: (creator.iuAllocation / 100) * totalCreatorAllocation * Number(formData.totalInnovationUnits) / 100
      }));

      // Prepare data for API
      const projectData = {
        // Project Overview
        title: formData.projectName,
        shortDescription: formData.shortDescription,
        detailedDescription: formData.detailedDescription,
        relevantUrls: formData.relevantUrls.filter(url => url),

        // Tokenomics & Investment Details
        totalInnovationUnits: Number(formData.totalInnovationUnits),
        pricePerUnit: Number(formData.pricePerUnit),
        ownershipDistribution: {
          contributors: Number(formData.ownershipDistribution.contributors),
          investors: Number(formData.ownershipDistribution.investors),
          creators: Number(formData.ownershipDistribution.creators)
        },
        vestingSchedule: formData.vestingSchedule,
        investmentCap: Number(formData.investmentCap) || undefined,

        // Domain & Target Audience
        primaryDomain: formData.primaryDomain,
        subdomains: formData.subdomains,
        targetedPlatforms: formData.targetedPlatforms.filter(platform => platform),
        intendedAudience: formData.intendedAudience,

        // Team & Contributors
        creators: allCreators,

        // Collaboration & Access
        openForCollaboration: formData.openForCollaboration,
        contributionGuidelines: formData.contributionGuidelines,

        // Additional Information
        projectRoadmap: formData.projectRoadmap,
        riskAssessment: formData.riskAssessment,
        budgetAllocation: formData.budgetAllocation,
        contactInformation: formData.contactInformation,

        // Files
        files: fileUrls,

        // Draft status
        isDraft: formData.isDraft,

        // Status
        status: formData.isDraft ? 'draft' : 'active'
      };

      // FIRST execute smart contract transaction, only create API project if transaction succeeds
      if (isReady) {
        // Declare pendingToastId at this scope level so it's accessible in both try and catch blocks
        let pendingToastId;
        
        try {
          /////////////////////////////////////////////////////////////
          //////////   InnovationUnits.createProject   ////////////////
          /////////////////////////////////////////////////////////////
          
          // Format data for blockchain transaction
          const initialPrice = ethers.utils.parseEther(formData.pricePerUnit.toString());
          
          // Get creator addresses and shares
          const creatorAddresses = allCreatorsRelative.map(creator => creator.walletAddress);
          
          // Convert relative percentages (out of 100%) to basis points (out of 10000)
          const creatorShares = allCreatorsRelative.map(creator => 
            Math.round(Number(creator.iuAllocation) * 100)
          );
          
          // Convert percentage allocations to basis points (multiply by 100)
          const creatorAllocation = Math.round(Number(formData.ownershipDistribution.creators) * 100);
          const contributorAllocation = Math.round(Number(formData.ownershipDistribution.contributors) * 100);
          const investorAllocation = Math.round(Number(formData.ownershipDistribution.investors) * 100);
          
          // Show pending transaction toast
          pendingToastId = toast.loading(
            <div>
              <p className="font-bold">Creating Project...</p>
              <p className="text-sm mt-1">Please confirm the transaction in your wallet and wait for it to be mined.</p>
              <p className="text-xs mt-2 text-blue-600">This may take a few moments. Please don't close this page.</p>
            </div>,
            {
              position: "top-center",
              closeButton: false,
              closeOnClick: false,
              draggable: false,
            }
          );
          
          // Execute the smart contract transaction
          const result = await executeContractTransaction(
            services.innovationUnits.createProject.bind(services.innovationUnits),
            [
              initialPrice,
              creatorAddresses,
              creatorShares,
              creatorAllocation,
              contributorAllocation,
              investorAllocation,
              formData.projectName
            ],
            {
              pendingMessage: 'Creating project on blockchain...', 
              successMessage: 'Project created successfully on blockchain!',
              failMessage: 'Failed to create project on blockchain',
              showToast: false // Don't show the default toast, we're handling it manually
            }
          );
          
          // Close the pending toast
          toast.dismiss(pendingToastId);
          
          // If smart contract transaction successful, add transaction data to project and create API record
          if (result && result.hash) {
           
            // Add blockchain transaction details to project data
            projectData.transactionHash = result.hash;
            projectData.projectTokenId = result.projectId || result.events?.[0]?.args?.projectId?.toString();
            
            // NOW create the project record in the API database
            const response = await projectAPI.create(projectData);
              
            // Display toast with transaction hash and link to block explorer
            const networkName = chainId === 10 ? 'optimistic' : 'sepolia-optimism';
            const explorerUrl = `https://${networkName}.etherscan.io/tx/${result.hash}`;
            
            toast.success(
              <div>
                <p className="font-bold">Project Created Successfully!</p>
                <p className="text-sm mt-1">Your project has been created on the blockchain.</p>
                <div className="text-xs mt-2 flex items-center gap-2">
                  <span className="font-medium">Transaction: </span>
                  <div className="flex items-center space-x-2">
                    <a 
                      href={explorerUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline flex items-center"
                    >
                      <span className="mr-1">{result.hash.substring(0, 10)}...{result.hash.substring(result.hash.length - 6)}</span>
                      <ExternalLink size={12} />
                    </a>
                    <button 
                      onClick={() => copyToClipboard(result.hash)}
                      className="p-1 rounded-md hover:bg-blue-100 transition-colors"
                      title="Copy transaction hash"
                    >
                      <Copy size={14} className="text-blue-500" />
                    </button>
                  </div>
                </div>
              </div>,
              {
                autoClose: false, // Don't auto close
                closeButton: true,
                position: "top-center",
                className: "custom-toast-success",
                icon: "🎉"
              }
            );
            
            // Call success callback provided by parent component
            onSubmitSuccess();
          }
        } catch (contractError) {
          console.error('Smart contract interaction error:', contractError);
          
          // Dismiss any pending toast
          if (pendingToastId) {
            toast.dismiss(pendingToastId);
          }
          
          // Show error toast with details
          toast.error(
            <div>
              <p className="font-bold">Transaction Failed</p>
              <p className="text-sm mt-1">
                {contractError.reason || contractError.message || 'Failed to create project on blockchain'}
              </p>
              {contractError.transaction && contractError.transaction.hash && (
                <div className="text-xs mt-2 flex items-center gap-2">
                  <span className="font-medium">Transaction: </span>
                  <div className="flex items-center space-x-2">
                    <a 
                      href={`https://${chainId === 10 ? 'optimistic' : 'sepolia-optimism'}.etherscan.io/tx/${contractError.transaction.hash}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline flex items-center"
                    >
                      <span className="mr-1">
                        {contractError.transaction.hash.substring(0, 10)}...
                        {contractError.transaction.hash.substring(contractError.transaction.hash.length - 6)}
                      </span>
                      <ExternalLink size={12} />
                    </a>
                    <button 
                      onClick={() => copyToClipboard(contractError.transaction.hash)}
                      className="p-1 rounded-md hover:bg-red-100 transition-colors"
                      title="Copy transaction hash"
                    >
                      <Copy size={14} className="text-blue-500" />
                    </button>
                  </div>
                </div>
              )}
              <p className="text-xs mt-2 text-red-600">
                Project creation failed. No project information was saved.
              </p>
            </div>,
            {
              autoClose: false,
              closeButton: true,
              position: "top-center",
              className: "custom-toast-error"
            }
          );
        }
      } else {
        // If blockchain features not ready, show an error
        toast.error("Smart contract services are not ready. Please connect your wallet and try again.");
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Error submitting project');
      toast.error(`Error creating project: ${error.response?.data?.message || error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUrlChange = (index, value) => {
    const newUrls = [...formData.relevantUrls];
    newUrls[index] = value;
    setFormData(prev => ({
      ...prev,
      relevantUrls: newUrls
    }));
  };

  const addUrlField = () => {
    setFormData(prev => ({
      ...prev,
      relevantUrls: [...prev.relevantUrls, '']
    }));
  };

  const handleNext = () => {
    // Validate current step before proceeding
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      // Show error toast if validation fails
      if (activeStep === 0) {
        toast.error('Please provide a Project Title before proceeding');
        setValidationErrors(prev => ({
          ...prev,
          projectName: !formData.projectName.trim() ? 'Project Title is required' : ''
        }));
      } else if (activeStep === 1) {
        // Check which field is causing the validation to fail
        if (!formData.pricePerUnit || Number(formData.pricePerUnit) <= 0) {
          toast.error('Please provide a valid Initial Price before proceeding');
          setValidationErrors(prev => ({
            ...prev,
            pricePerUnit: 'Initial Price must be greater than 0'
          }));
        } else {
          // Must be the ownership distribution
          const creators = Number(formData.ownershipDistribution.creators) || 0;
          const investors = Number(formData.ownershipDistribution.investors) || 0;
          const contributors = Number(formData.ownershipDistribution.contributors) || 0;
          const total = creators + investors + contributors;
          
          toast.error(`Ownership distribution must equal 100%. Current total: ${total}%`);
          setValidationErrors(prev => ({
            ...prev,
            ownershipDistribution: `Total distribution must equal 100%. Current total: ${total}%`
          }));
        }
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Function to get filtered options for each creator dropdown
  const getFilteredInnovators = (index) => {
    const selectedInnovators = formData.creators.slice(0, index).map(creator => creator.innovatorId);
    return registeredInnovators.filter(innovator => !selectedInnovators.includes(innovator.value));
  };

  // Function to copy text to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.info('Transaction hash copied to clipboard', {
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: true,
        icon: <Check size={16} />
      });
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <div className="p-6 border-gray-400 border bg-gray-100 rounded-lg shadow-md ">
      <div className="flex justify-center mb-8">
        <div className="flex space-x-4">
          <div className={`w-8 h-8 flex items-center justify-center rounded-full ${activeStep === 0 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-700'}`}>1</div>
          <div className={`w-8 h-8 flex items-center justify-center rounded-full ${activeStep === 1 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-700'}`}>2</div>
          <div className={`w-8 h-8 flex items-center justify-center rounded-full ${activeStep === 2 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-700'}`}>3</div>
        </div>
      </div>

      <form className='space-y-8 '>
        {activeStep === 0 && (
          <div className='border-b border-gray-200 pb-6 animate-fadeIn'>
            <h2 className='text-xl font-semibold text-gray-800 mb-4'>
              Project Overview
            </h2>

            {/* Project Name */}
            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700'>
                Project Name *
              </label>
              <input
                type='text'
                name='projectName'
                value={formData.projectName}
                onChange={handleChange}
                className={`mt-1 p-1 block w-full h-10 rounded-md border-${validationErrors.projectName ? 'red-500' : 'gray-400'} shadow-sm focus:border-blue-500 focus:ring-blue-500 ${validationErrors.projectName ? 'bg-red-50' : ''}`}
                required
              />
              {validationErrors.projectName && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.projectName}</p>
              )}
            </div>

            {/* Short Description */}
            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700'>
                Short Description *
                <span className='text-xs text-gray-500 ml-1'>
                  (max 280 characters)
                </span>
              </label>
              <textarea
                name='shortDescription'
                value={formData.shortDescription}
                onChange={handleChange}
                maxLength={280}
                rows={2}
                className='mt-1 p-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500'
                required
              />
              <div className='text-xs text-gray-500 mt-1'>
                {formData.shortDescription.length}/280 characters
              </div>
            </div>

            {/* Detailed Description */}
            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700'>
                Detailed Description *
              </label>
              <textarea
                name='detailedDescription'
                value={formData.detailedDescription}
                onChange={handleChange}
                rows={6}
                className='mt-1 p-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500'
                required
              />
            </div>

            {/* Relevant URLs */}
            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Project URLs
              </label>
              {formData.relevantUrls.map((url, index) => (
                <div key={index} className='flex gap-2 mb-2'>
                  <input
                    type='url'
                    placeholder='https://example.com'
                    value={url}
                    onChange={(e) => handleUrlChange(index, e.target.value)}
                    className='mt-1 p-1 block w-full h-10 rounded-md border-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500'
                  />
                  {index > 0 && (
                    <button
                      type='button'
                      onClick={() => removeArrayItem('relevantUrls', index)}
                      className='rounded-md bg-red-100 p-2 text-red-600 hover:bg-red-200'
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type='button'
                onClick={addUrlField}
                className='mt-2 inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              >
                + Add URL
              </button>
            </div>
          </div>
        )}

        {activeStep === 1 && (
          <div className='border-b border-gray-200 pb-6 animate-fadeIn'>
            <h2 className='text-xl font-semibold text-gray-800 mb-4'>
              Tokenomics & Ownership Configuration
            </h2>

            {/* Total Innovation Units - Changed to display only */}
            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700'>
                Total Innovation Units
              </label>
              <div className="flex items-center">
                <input
                  type='text'
                  value={formData.totalInnovationUnits}
                  disabled
                  className='mt-1 p-1 block w-full h-10 rounded-md border-gray-400 shadow-sm bg-gray-100'
                />
                {loadingContractData && (
                  <span className="ml-2 text-sm text-gray-500">Loading...</span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                This value is set by the protocol and cannot be changed.
              </p>
            </div>

            {/* Price per Innovation Unit */}
            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700'>
                Innovation Unit Initial Price (CEL) *
              </label>
              <input
                type='number'
                step='0.0001'
                name='pricePerUnit'
                value={formData.pricePerUnit}
                onChange={handleChange}
                className={`mt-1 p-1 block w-full h-10 rounded-md border-${validationErrors.pricePerUnit ? 'red-500' : 'gray-400'} shadow-sm focus:border-blue-500 focus:ring-blue-500 ${validationErrors.pricePerUnit ? 'bg-red-50' : ''}`}
                required
              />
              {validationErrors.pricePerUnit && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.pricePerUnit}</p>
              )}
            </div>

            {/* Ownership Distribution */}
            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Ownership Distribution *
              </label>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                {/* Reserved for Contributors */}
                <div>
                  <label className='block text-xs text-gray-500 mb-1'>
                    Reserved for Contributors (%)
                  </label>
                  <input
                    type='number'
                    min='0'
                    max='100'
                    value={formData.ownershipDistribution.contributors}
                    onChange={(e) =>
                      handleDistributionChange(
                        'contributors',
                        e.target.value
                      )
                    }
                    className={`mt-1 p-1 block w-full h-10 rounded-md border-${validationErrors.ownershipDistribution ? 'red-500' : 'gray-400'} shadow-sm focus:border-blue-500 focus:ring-blue-500 ${validationErrors.ownershipDistribution ? 'bg-red-50' : ''}`}
                    required
                  />
                </div>

                {/* Reserved for Investors */}
                <div>
                  <label className='block text-xs text-gray-500 mb-1'>
                    Reserved for Investors (%)
                  </label>
                  <input
                    type='number'
                    min='0'
                    max='100'
                    value={formData.ownershipDistribution.investors}
                    onChange={(e) =>
                      handleDistributionChange(
                        'investors',
                        e.target.value
                      )
                    }
                    className={`mt-1 p-1 block w-full h-10 rounded-md border-${validationErrors.ownershipDistribution ? 'red-500' : 'gray-400'} shadow-sm focus:border-blue-500 focus:ring-blue-500 ${validationErrors.ownershipDistribution ? 'bg-red-50' : ''}`}
                    required
                  />
                </div>

                {/* Creators Allocation */}
                <div>
                  <label className='block text-xs text-gray-500 mb-1'>
                    Creators Allocation (%)
                  </label>
                  <input
                    type='number'
                    min='0'
                    max='100'
                    value={formData.ownershipDistribution.creators}
                    onChange={(e) =>
                      handleDistributionChange(
                        'creators',
                        e.target.value
                      )
                    }
                    className={`mt-1 p-1 block w-full h-10 rounded-md border-${validationErrors.ownershipDistribution ? 'red-500' : 'gray-400'} shadow-sm focus:border-blue-500 focus:ring-blue-500 ${validationErrors.ownershipDistribution ? 'bg-red-50' : ''}`}
                    required
                  />
                </div>
              </div>
              
              {/* Display total and validation message */}
              <div className="mt-2">
                <div className={`text-sm ${validationErrors.ownershipDistribution ? 'text-red-500' : 'text-gray-600'} font-medium`}>
                  Total: {
                    (Number(formData.ownershipDistribution.contributors) || 0) + 
                    (Number(formData.ownershipDistribution.investors) || 0) + 
                    (Number(formData.ownershipDistribution.creators) || 0)
                  }%
                </div>
                {validationErrors.ownershipDistribution && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.ownershipDistribution}</p>
                )}
              </div>
            </div>

            {/* ========== 4. TEAM & CREATORS ========== */}
            <div className='border-b border-gray-200 pb-6'>
              <h2 className='text-xl font-semibold text-gray-800 mb-4'>
                Project Creators
              </h2>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Creator IUs Allocation: {remainingCreatorIUs} / {totalCreatorIUs}
                </label>
              </div>

              {/* Add a default contributor section for the product owner */}
              <div className='border-b border-gray-200 pb-6  p-4 border border-gray-200 rounded-md mb-4'>
                <h2 className='text-md  font-medium text-gray-800 mb-4'>
                  Creator # 1 (Project Owner)
                </h2>
                <div className='mb-4'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-2'>
                    {/* Display Product Owner Name */}
                    <div>
                      <label className='block text-sm font-medium text-gray-700'>
                        Name
                      </label>
                      <input
                        type='text'
                        value={currentInnovator?.name || ''}
                        disabled
                        className='mt-1 p-1 block w-full h-10 rounded-md border-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500'
                      />
                    </div>

                    {/* Display Wallet Address */}
                    <div>
                      <label className='block text-sm font-medium text-gray-700'>
                        Wallet Address
                      </label>
                      <input
                        type='text'
                        value={currentInnovator?.walletAddress || ''}
                        disabled
                        className='mt-1 p-1 block w-full h-10 rounded-md border-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500'
                      />
                    </div>
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-2'>
                    {/* Role Selection */}
                    <div>
                      <label className='block text-sm font-medium text-gray-700'>
                        Role
                      </label>
                      <Select
                        options={ROLES.map(role => ({ value: role, label: role }))}
                        value={ROLES.map(role => ({ value: role, label: role })).find(option => option.value === currentInnovator?.role)}
                        onChange={(value) => setCurrentInnovator(prev => ({ ...prev, role: value.value }))}
                        className='mt-1'
                        isClearable
                        isSearchable
                        placeholder='Select role...'
                      />
                    </div>

                    {/* Auto-calculated allocation percentage */}
                    <div>
                      <label className='block text-sm font-medium text-gray-700'>
                        Share of Creator Allocation
                      </label>
                      <div className="flex items-center">
                        <input
                          type='number'
                          value={primaryCreatorAllocation}
                          disabled
                          className='mt-1 p-1 block w-full h-10 rounded-md border-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-gray-100'
                        />
                        <span className="ml-2">%</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Auto-calculated based on other creators' allocations
                      </p>
                    </div>
                  </div>
                </div>
              </div>

             
              {/* Additional creators */}
              {formData?.creators?.map((creator, index) => (
                <div
                  key={index}
                  className='mb-4 p-4 border border-gray-200 rounded-md'
                >
                  <div className='flex justify-between items-center mb-3'>
                    <h3 className='text-md font-medium'>Creator # {index + 2}</h3>
                    <button
                      type='button'
                      onClick={() => removeCreator(index)}
                      className='text-red-500 hover:text-red-700'
                    >
                      Remove
                    </button>
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-2'>
                    {/* Innovator Selection */}
                    <div>
                      <label className='block text-sm font-medium text-gray-700'>
                        Select Innovator
                      </label>
                      <Select
                        options={getFilteredInnovators(index)}
                        value={registeredInnovators.find(i => i.value === creator.innovatorId)}
                        onChange={(value) => handleCreatorChange(index, 'innovator', value)}
                        className="mt-1"
                        isClearable
                        isSearchable
                        placeholder="Search and select innovator..."
                      />
                    </div>

                    {/* Role */}
                    <div>
                      <label className='block text-sm font-medium text-gray-700'>
                        Role
                      </label>
                      <Select
                        options={ROLES.map(role => ({ value: role, label: role }))}
                        value={ROLES.map(role => ({ value: role, label: role })).find(option => option.value === creator.role)}
                        onChange={(value) => handleCreatorChange(index, 'role', value.value)}
                        className='mt-1'
                        isClearable
                        isSearchable
                        placeholder='Select role...'
                      />
                    </div>
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-2'>
                    {/* Display Wallet Address */}
                    <div>
                      <label className='block text-sm font-medium text-gray-700'>
                        Wallet Address
                      </label>
                      <input
                        type='text'
                        value={creator.walletAddress}
                        disabled
                        className='mt-1 p-1 block w-full h-10 rounded-md border-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500'
                      />
                    </div>

                    {/* Share of Creator Allocation */}
                    <div>
                      <label className='block text-sm font-medium text-gray-700'>
                        Share of Creator Allocation
                      </label>
                      <div className="flex items-center">
                        <input
                          type='number'
                          value={creator.iuAllocation}
                          onChange={(e) =>
                            handleCreatorChange(index, 'iuAllocation', e.target.value)
                          }
                          max={primaryCreatorAllocation + Number(creator.iuAllocation || 0)}
                          className='mt-1 p-1 block w-full h-10 rounded-md border-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500'
                          placeholder={`Max: ${primaryCreatorAllocation + Number(creator.iuAllocation || 0)}%`}
                        />
                        <span className="ml-2">%</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Percentage of the total creator allocation ({formData.ownershipDistribution.creators}% of project)
                      </p>
                    </div>
                  </div>
                </div>           
              ))}

              <button
                type='button'
                onClick={addCreator}
                disabled={primaryCreatorAllocation <= 0}
                className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                + Add Creator
              </button>
            </div>


              {/* Creator Allocation Summary */}
              <div className="mb-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Creator Allocation Distribution Summary
                </label>
                <div className="flex flex-col space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Project Owner:</span>
                    <span className="font-medium">{primaryCreatorAllocation}% of creators' allocation</span>
                  </div>
                  
                  {formData.creators.map((creator, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-sm">{creator.name || `Creator #${index + 2}`}:</span>
                      <span className="font-medium">{creator.iuAllocation || 0}% of creators' allocation</span>
                    </div>
                  ))}
                  
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Total:</span>
                      <span className="font-medium">
                        {primaryCreatorAllocation + formData.creators.reduce((sum, c) => sum + Number(c.iuAllocation || 0), 0)}% 
                        {primaryCreatorAllocation + formData.creators.reduce((sum, c) => sum + Number(c.iuAllocation || 0), 0) !== 100 && 
                          <span className="text-red-500 ml-2">(Must equal 100%)</span>
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>

          </div>
        )}

        {activeStep === 2 && (
          <div className='border-b border-gray-200 pb-6 animate-fadeIn'>
            <h2 className='text-xl font-semibold text-gray-800 mb-4'>
              Domain & Target Audience
            </h2>
            <p className='text-sm text-gray-600 mb-4'>Please provide detailed information about your project's domain and target audience to help us better understand your goals and objectives.</p>

            {/* Primary Domain */}
            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700'>
                Primary Domain *
              </label>
              <Select
                options={DOMAINS.map(domain => ({ value: domain, label: domain }))}
                value={DOMAINS.map(domain => ({ value: domain, label: domain }))?.find(option => option?.value === formData?.primaryDomain)}
                onChange={(value) => handleChange({ target: { name: 'primaryDomain', value: value?.value } })}
                className='mt-1'
                isClearable
                isSearchable
                placeholder='Select a domain...'
              />
            </div>

            {/* Targeted Platforms/Protocols */}
            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Targeted Platforms/Protocols
              </label>
              {formData.targetedPlatforms.map((platform, index) => (
                <div key={index} className='flex gap-2 mb-2'>
                  <input
                    type='text'
                    placeholder='e.g., Ethereum, Uniswap, etc.'
                    value={platform}
                    onChange={(e) =>
                      handleArrayChange('targetedPlatforms', index, e.target.value)
                    }
                    className='mt-1 p-1 block w-full h-10 rounded-md border-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500'
                  />
                  {index > 0 && (
                    <button
                      type='button'
                      onClick={() => removeArrayItem('targetedPlatforms', index)}
                      className='rounded-md bg-red-100 p-2 text-red-600 hover:bg-red-200'
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type='button'
                onClick={() => addArrayItem('targetedPlatforms', '')}
                className='mt-2 inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              >
                + Add Platform
              </button>
            </div>

            {/* Intended Audience */}
            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Intended Audience
              </label>
              <div className='flex flex-wrap gap-2'>
                {[
                  'Developers',
                  'Researchers',
                  'Enterprises',
                ].map((audience) => (
                  <div key={audience} className='flex items-center'>
                    <input
                      type='checkbox'
                      id={`audience-${audience}`}
                      checked={formData.intendedAudience.includes(audience)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData((prev) => ({
                            ...prev,
                            intendedAudience: [...prev.intendedAudience, audience],
                          }));
                        } else {
                          setFormData((prev) => ({
                            ...prev,
                            intendedAudience: prev.intendedAudience.filter(
                              (item) => item !== audience
                            ),
                          }));
                        }
                      }}
                      className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                    />
                    <label
                      htmlFor={`audience-${audience}`}
                      className='ml-2 text-sm text-gray-700'
                    >
                      {audience}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}


        {/* ========== 8. SUBMISSION CONTROLS ========== */}
        <div className='flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0'>
          <button
            onClick={() => navigate('/projects')}
            className="py-2 px-4 rounded-lg bg-red-400 text-white hover:text-gray-800"
          >
            Cancel
          </button>

          <div className='flex space-x-4'>
            {activeStep > 0 && (
              <button
                type='button'
                onClick={handleBack}
                className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              >
                Back
              </button>
            )}
            {activeStep < 2 ? (
              <button
                type='button'
                onClick={handleNext}
                className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-300 ease-in-out transform hover:scale-105'
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md ${loading ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-300 ease-in-out transform hover:scale-105`}
              >
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

export default ProjectForm;
